import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { addDays, format, startOfWeek } from 'date-fns';
import {
  AddRounded,
  ArrowForwardRounded,
  AutoAwesomeRounded,
  BoltRounded,
  CalendarMonthRounded,
  CheckCircleRounded,
  ChevronRightRounded,
  DeleteOutlineRounded,
  EditOutlined,
  InsightsRounded,
  LocalFireDepartmentRounded,
  RestaurantMenuRounded,
  RestaurantRounded,
  ScaleRounded,
  ShoppingBasketRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AIFoodAnalyzer from '../AIFoodAnalyzer';
import BodyHistoryChart from '../Dashboard/BodyHistoryChart';
import QuickMeasurementDialog from '../Dashboard/QuickMeasurementDialog';
import OnboardingPromptModal from '../Onboarding/OnboardingPromptModal';
import { useAuth } from '../../context/AuthContext';
import { useWeeklyMenu } from '../MenuSemanal/useWeeklyMenu';
import AddMealPopup from '../MenuSemanal/AddMealPopup';
import useDashboardV2Data from './useDashboardV2Data';
import ExperimentalPopupProvider from '../ExperimentalPopups/ExperimentalPopupProvider';
import './DashboardV2.css';

const number = (value) => Number(value) || 0;

const emptyMacros = { kilocalories: 0, protein: 0, carbs: 0, fat: 0 };

const getPreviewImage = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';
  return `${baseUrl}/${path.replace(/\\/g, '/').replace(/^\//, '')}`;
};

const createPreviewMenu = () => {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  const meal = (items = [], macros = emptyMacros) => ({ items, macros });
  const food = (id, name, macros, extra = {}) => ({
    _id: id,
    name,
    type: 'planned-recipe',
    quantity: 1,
    unit: 'ración',
    ...macros,
    ...extra,
  });
  const catalog = [
    food('preview-1', 'Porridge de avena, plátano y canela', { kilocalories: 390, protein: 17, carbs: 62, fat: 9 }),
    food('preview-2', 'Bowl mediterráneo de pollo', { kilocalories: 610, protein: 44, carbs: 68, fat: 19 }),
    food('preview-3', 'Salmón al horno con verduras', { kilocalories: 540, protein: 39, carbs: 35, fat: 25 }),
    food('preview-4', 'Tostada integral con aguacate', { kilocalories: 310, protein: 12, carbs: 38, fat: 13 }),
    food('preview-5', 'Yogur con frutos rojos', { kilocalories: 190, protein: 14, carbs: 22, fat: 6 }),
  ];
  const weeklyData = {};

  for (let offset = 0; offset < 7; offset += 1) {
    const date = addDays(monday, offset);
    const key = format(date, 'yyyy-MM-dd');
    const first = catalog[offset % catalog.length];
    const second = catalog[(offset + 1) % catalog.length];
    const third = catalog[(offset + 2) % catalog.length];
    const meals = {
      Desayuno: meal(offset === 6 ? [] : [first], offset === 6 ? emptyMacros : first),
      'Media mañana': meal(offset % 2 === 0 ? [catalog[4]] : [], offset % 2 === 0 ? catalog[4] : emptyMacros),
      Comida: meal(offset === 6 ? [] : [second], offset === 6 ? emptyMacros : second),
      'Media tarde': meal([], emptyMacros),
      Cena: meal(offset > 4 ? [] : [third], offset > 4 ? emptyMacros : third),
    };
    const totals = Object.values(meals).reduce((sum, current) => ({
      kilocalories: sum.kilocalories + number(current.macros.kilocalories),
      protein: sum.protein + number(current.macros.protein),
      carbs: sum.carbs + number(current.macros.carbs),
      fat: sum.fat + number(current.macros.fat),
    }), { ...emptyMacros });
    weeklyData[key] = { date, meals, totals };
  }

  return {
    currentWeekStart: monday,
    weeklyData,
    loading: false,
    error: null,
    actions: {
      addFoodToMeal: async () => {},
      removeFoodFromMeal: async () => {},
      refresh: async () => {},
    },
  };
};

const PREVIEW_DATA = {
  ...createPreviewMenu(),
  userInfo: { name: 'Lucía', macros: { kilocalories: 2100, protein: 140, carbs: 230, fat: 70 } },
  userMetrics: [],
  hasCompletedOnboarding: true,
  showOnboardingModal: false,
  dismissOnboardingModal: () => {},
};

const mealMeta = (name = '') => {
  const normalized = name.toLowerCase().trim();
  if (normalized.includes('desayuno')) return { icon: '☀', tone: 'amber', label: 'Desayuno' };
  if (normalized.includes('media mañana') || normalized.includes('almuerzo')) return { icon: '●', tone: 'lime', label: name };
  if (normalized.includes('comida')) return { icon: '◐', tone: 'terracotta', label: 'Comida' };
  if (normalized.includes('merienda') || normalized.includes('media tarde')) return { icon: '✦', tone: 'rose', label: name };
  if (normalized.includes('cena')) return { icon: '☾', tone: 'blue', label: 'Cena' };
  return { icon: '+', tone: 'slate', label: name || 'Extra' };
};

const PageHeader = ({ name, date }) => (
  <header className="dash2-header">
    <div>
      <p className="dash2-eyebrow">{date}</p>
      <h1>Hola, {name}</h1>
      <p className="dash2-header-copy">Tu alimentación de hoy, ordenada y fácil de entender.</p>
    </div>
  </header>
);

const MobileQuickActions = ({ onAddMeal, onMeasurement }) => (
  <div className="dash2-mobile-actions" aria-label="Acciones rápidas">
    <button type="button" onClick={onAddMeal}>
      <RestaurantRounded />
      <span><strong>Comida</strong><small>Registrar ahora</small></span>
    </button>
    <button type="button" onClick={onMeasurement}>
      <ScaleRounded />
      <span><strong>Medición</strong><small>Añadir dato</small></span>
    </button>
  </div>
);

const CalorieRing = ({ current, target, exceeded }) => {
  const safeTarget = Math.max(number(target), 1);
  const percent = Math.min((number(current) / safeTarget) * 100, 100);
  const circumference = 289;

  return (
    <div className={`dash2-ring${exceeded ? ' dash2-ring--warning' : ''}`} aria-label={`${current} de ${target} kilocalorías`}>
      <svg viewBox="0 0 112 112" aria-hidden="true">
        <circle className="dash2-ring-track" cx="56" cy="56" r="46" />
        <circle
          className="dash2-ring-progress"
          cx="56"
          cy="56"
          r="46"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (circumference * percent) / 100}
        />
      </svg>
      <div className="dash2-ring-value">
        <strong>{number(current).toLocaleString('es-ES')}</strong>
        <span>de {safeTarget.toLocaleString('es-ES')} kcal</span>
      </div>
    </div>
  );
};

const MacroMeter = ({ label, value, target, tone }) => {
  const progress = Math.min((number(value) / Math.max(number(target), 1)) * 100, 100);
  const exceeded = number(value) > number(target);
  const difference = number(value) - number(target);
  return (
    <div className={`dash2-macro dash2-macro--${tone}${exceeded ? ' dash2-macro--warning' : ''}`}>
      <div className="dash2-macro-topline">
        <span>{label}</span>
        <strong>{exceeded && <WarningAmberRounded />} {number(value)}g</strong>
      </div>
      <div className="dash2-macro-track">
        <span style={{ width: `${progress}%` }} />
      </div>
      <small>{exceeded ? `${difference}g por encima` : `Objetivo ${number(target)}g`}</small>
    </div>
  );
};

const TodayOverview = ({ macros, goals, onAddMeal }) => {
  const calorieGoal = number(goals?.kilocalories || goals?.calories) || 2000;
  const remaining = Math.max(calorieGoal - number(macros.kilocalories), 0);
  const exceeded = number(macros.kilocalories) > calorieGoal;
  const macroExceeded = number(macros.protein) > (number(goals?.protein) || 150)
    || number(macros.carbs) > (number(goals?.carbs) || 220)
    || number(macros.fat) > (number(goals?.fat) || 70);
  const hasWarning = exceeded || macroExceeded;

  return (
    <section className={`dash2-overview${hasWarning ? ' dash2-overview--warning' : ''}`}>
      <div className="dash2-overview-copy">
        <span className={`dash2-live-pill${hasWarning ? ' dash2-live-pill--warning' : ''}`}>
          {hasWarning ? <WarningAmberRounded /> : <span />} {hasWarning ? 'Revisa tus objetivos' : 'Hoy'}
        </span>
        <h2>{exceeded ? 'Objetivo superado' : `${remaining.toLocaleString('es-ES')} kcal disponibles`}</h2>
        <p>
          {exceeded
            ? `Has superado tu referencia en ${number(macros.kilocalories) - calorieGoal} kcal. Sin juicios: mañana seguimos.`
            : 'Vas construyendo el día a tu ritmo. Registra lo que comas y nosotros hacemos las cuentas.'}
        </p>
        <button className="dash2-primary-action" type="button" onClick={onAddMeal}>
          <RestaurantRounded /> Registrar comida
        </button>
      </div>
      <div className="dash2-overview-data">
        <CalorieRing current={macros.kilocalories} target={calorieGoal} exceeded={exceeded} />
        <div className="dash2-macro-list">
          <MacroMeter label="Proteína" value={macros.protein} target={number(goals?.protein) || 150} tone="protein" />
          <MacroMeter label="Carbohidratos" value={macros.carbs} target={number(goals?.carbs) || 220} tone="carbs" />
          <MacroMeter label="Grasas" value={macros.fat} target={number(goals?.fat) || 70} tone="fat" />
        </div>
      </div>
    </section>
  );
};

const MealItem = ({ item, mealName, onEditMeal, onRemoveMeal }) => {
  const navigate = useNavigate();
  const image = getPreviewImage(item.mainImage || item.aiAnalysis?.imageUrl);
  const isStock = item.type === 'planned-stock';
  const recipeId = isStock ? item.sourceRecipeId : item._id;
  const canOpen = Boolean((item.type === 'planned-recipe' && item._id) || (isStock && item.sourceRecipeId));

  const openRecipe = () => {
    if (canOpen) navigate(`/recetas/${recipeId}`);
  };

  return (
    <article
      className={`dash2-food-row${canOpen ? ' is-clickable' : ''}`}
      onClick={openRecipe}
      role={canOpen ? 'link' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onKeyDown={(event) => {
        if (canOpen && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          openRecipe();
        }
      }}
    >
      {image ? <img src={image} alt="" /> : <span className="dash2-food-placeholder"><RestaurantMenuRounded /></span>}
      <div className="dash2-food-copy">
        <strong>{item.name}</strong>
        <span>{item.quantity} {item.unit}</span>
      </div>
      <small>{Math.round(number(item.kilocalories))} kcal</small>
      <div className="dash2-food-actions">
        {item.type === 'unplanned' && (
          <button
            type="button"
            aria-label={`Editar ${item.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onEditMeal(item, mealName);
            }}
          >
            <EditOutlined />
          </button>
        )}
        <button
          type="button"
          aria-label={`Eliminar ${item.name}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemoveMeal(item._id, mealName);
          }}
        >
          <DeleteOutlineRounded />
        </button>
      </div>
    </article>
  );
};

const TodaySchedule = ({ meals, error, onAddMeal, onEditMeal, onRemoveMeal }) => (
  <section className="dash2-card dash2-schedule">
    <div className="dash2-section-heading">
      <div>
        <span className="dash2-kicker">Agenda nutricional</span>
        <h2>Lo que comes hoy</h2>
      </div>
      <Link to="/menu-semanal">Abrir plan <ChevronRightRounded /></Link>
    </div>
    {error && <p className="dash2-schedule-error">{error}</p>}
    <div className="dash2-timeline">
      {meals.map(([mealName, mealData]) => {
        const meta = mealMeta(mealName);
        const items = mealData?.items || [];
        return (
          <article className="dash2-meal" key={mealName}>
            <div className={`dash2-meal-marker dash2-meal-marker--${meta.tone}`}>{meta.icon}</div>
            <div className="dash2-meal-content">
              <div className="dash2-meal-heading">
                <div>
                  <h3>{meta.label}</h3>
                  <span>{items.length} {items.length === 1 ? 'alimento' : 'alimentos'}</span>
                </div>
                <div className="dash2-meal-heading-actions">
                  <div className="dash2-meal-total">
                    <strong>{Math.round(number(mealData?.macros?.kilocalories))}</strong>
                    <span>kcal</span>
                  </div>
                  <button className="dash2-meal-add" type="button" aria-label={`Añadir a ${mealName}`} onClick={() => onAddMeal(mealName)}>
                    <AddRounded /> Añadir
                  </button>
                </div>
              </div>
              <div className="dash2-food-list">
                {items.length ? items.map((item, index) => (
                  <MealItem
                    item={item}
                    mealName={mealName}
                    key={item._id || `${mealName}-${index}`}
                    onEditMeal={onEditMeal}
                    onRemoveMeal={onRemoveMeal}
                  />
                )) : (
                  <button className="dash2-meal-empty" type="button" onClick={() => onAddMeal(mealName)}>
                    Planificar {mealName.toLowerCase()}
                  </button>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  </section>
);

const ChartTooltip = ({ active, payload, label, metric }) => {
  if (!active || !payload?.length) return null;
  const labels = { calories: 'Calorías', protein: 'Proteína', carbs: 'Carbohidratos', fat: 'Grasas' };
  return (
    <div className="dash2-chart-tooltip">
      <span>{label}</span>
      {payload.map((entry) => (
        <strong key={entry.dataKey} style={{ color: entry.color }}>
          {labels[entry.dataKey] || entry.name}: {number(entry.value).toLocaleString('es-ES')} {metric === 'calories' ? 'kcal' : 'g'}
        </strong>
      ))}
    </div>
  );
};

const WeeklyChart = ({ data, goals }) => {
  const [metric, setMetric] = useState('calories');
  const average = Math.round(data.reduce((total, day) => total + number(day.calories), 0) / Math.max(data.length, 1));
  const loggedDays = data.filter((day) => number(day.calories) > 0).length;
  const calorieGoal = number(goals?.kilocalories || goals?.calories) || 2000;

  return (
    <section className="dash2-card dash2-weekly">
      <div className="dash2-section-heading">
        <div>
          <span className="dash2-kicker">Esta semana</span>
          <h2>Tu ritmo, no la perfección</h2>
        </div>
        <div className="dash2-trend-controls">
          <div className="dash2-trend-toggle" role="group" aria-label="Dato de tendencia">
            <button className={metric === 'calories' ? 'is-active' : ''} type="button" onClick={() => setMetric('calories')}>Kcal</button>
            <button className={metric === 'macros' ? 'is-active' : ''} type="button" onClick={() => setMetric('macros')}>Macros</button>
          </div>
          <div className="dash2-week-stat">
            <strong>{average.toLocaleString('es-ES')}</strong>
            <span>kcal de media</span>
          </div>
        </div>
      </div>
      {metric === 'macros' && (
        <div className="dash2-macro-legend" aria-label="Leyenda de macronutrientes">
          <span><i className="is-protein" /> Proteína</span>
          <span><i className="is-carbs" /> Carbohidratos</span>
          <span><i className="is-fat" /> Grasas</span>
        </div>
      )}
      <div className="dash2-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 18, right: 8, left: -25, bottom: 0 }}>
            <defs>
              <linearGradient id="dash2Area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#667eea" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#667eea" stopOpacity={0.01} />
              </linearGradient>
              <linearGradient id="dash2Protein" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4ecdc4" stopOpacity={0.24} />
                <stop offset="100%" stopColor="#4ecdc4" stopOpacity={0.01} />
              </linearGradient>
              <linearGradient id="dash2Carbs" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity={0.01} />
              </linearGradient>
              <linearGradient id="dash2Fat" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f093fb" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#f093fb" stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 5" />
            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12, fontWeight: 700 }} dy={8} />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
            <Tooltip content={<ChartTooltip metric={metric} />} cursor={{ stroke: '#cbd5e1', strokeDasharray: '3 4' }} />
            {metric === 'calories' ? (
              <>
                <ReferenceLine y={calorieGoal} stroke="#a5b4fc" strokeDasharray="5 5" />
                <Area type="monotone" dataKey="calories" stroke="#667eea" strokeWidth={3} fill="url(#dash2Area)" activeDot={{ r: 5, fill: '#4f46e5', stroke: '#fff', strokeWidth: 3 }} />
              </>
            ) : (
              <>
                <Area type="monotone" dataKey="protein" stroke="#4ecdc4" strokeWidth={2.5} fill="url(#dash2Protein)" activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="carbs" stroke="#fbbf24" strokeWidth={2.5} fill="url(#dash2Carbs)" activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="fat" stroke="#f093fb" strokeWidth={2.5} fill="url(#dash2Fat)" activeDot={{ r: 4 }} />
              </>
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="dash2-chart-note">
        <CheckCircleRounded /> Has registrado comidas {loggedDays} de 7 días esta semana.
      </div>
    </section>
  );
};

const NextStep = ({ hasMeals, onAddMeal, onMeasurement }) => (
  <section className="dash2-card dash2-next-step">
    <div className="dash2-next-icon"><BoltRounded /></div>
    <span className="dash2-kicker">Siguiente mejor acción</span>
    <h2>{hasMeals ? 'Mantén el registro al día' : 'Empieza por tu primera comida'}</h2>
    <p>{hasMeals ? 'Añade lo siguiente que comas. Solo te llevará unos segundos.' : 'Una foto o una breve descripción es suficiente para empezar.'}</p>
    <button type="button" onClick={onAddMeal}>Registrar comida <ArrowForwardRounded /></button>
    <button className="dash2-secondary-action" type="button" onClick={onMeasurement}><ScaleRounded /> Añadir medición</button>
  </section>
);

const QuickLinks = () => {
  const navigate = useNavigate();
  const links = [
    { label: 'Plan semanal', note: 'Organiza tus comidas', path: '/menu-semanal', icon: <CalendarMonthRounded />, tone: 'sage' },
    { label: 'Descubrir recetas', note: 'Ideas que encajan contigo', path: '/recetas', icon: <AutoAwesomeRounded />, tone: 'sand' },
    { label: 'Lista de compra', note: 'Todo lo que necesitas', path: '/listas', icon: <ShoppingBasketRounded />, tone: 'rose' },
  ];

  return (
    <section className="dash2-card dash2-quick-links">
      <span className="dash2-kicker">Atajos</span>
      <h2>Continúa por aquí</h2>
      <div>
        {links.map((item) => (
          <button type="button" onClick={() => navigate(item.path)} key={item.path}>
            <span className={`dash2-link-icon dash2-link-icon--${item.tone}`}>{item.icon}</span>
            <span><strong>{item.label}</strong><small>{item.note}</small></span>
            <ChevronRightRounded />
          </button>
        ))}
      </div>
    </section>
  );
};

const DashboardV2 = ({ preview = false }) => {
  const auth = useAuth();
  const liveMenu = useWeeklyMenu(auth);
  const liveData = useDashboardV2Data();
  const menu = preview ? PREVIEW_DATA : liveMenu;
  const [metricsRefreshKey, setMetricsRefreshKey] = useState(0);
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  const [measurementOpen, setMeasurementOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [addPopup, setAddPopup] = useState({ open: false, meal: null });
  const {
    userInfo,
    userMetrics,
    hasCompletedOnboarding,
    showOnboardingModal,
    dismissOnboardingModal,
  } = preview ? PREVIEW_DATA : liveData;

  const todayKey = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const today = menu.weeklyData?.[todayKey];
  // Los totales del hook semanal llegan sin redondear y arrastran residuos de coma
  // flotante de las actualizaciones optimistas (restar y sumar deja -8.88e-16).
  const todayMacros = useMemo(() => {
    const totals = today?.totals || emptyMacros;
    return {
      kilocalories: Math.round(number(totals.kilocalories)),
      protein: Math.round(number(totals.protein)),
      carbs: Math.round(number(totals.carbs)),
      fat: Math.round(number(totals.fat)),
    };
  }, [today]);
  const mealEntries = useMemo(() => Object.entries(today?.meals || {}), [today]);
  const weeklyStats = useMemo(() => ['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day, index) => {
    const dateKey = format(addDays(menu.currentWeekStart || new Date(), index), 'yyyy-MM-dd');
    const totals = menu.weeklyData?.[dateKey]?.totals || emptyMacros;
    return {
      day,
      calories: Math.round(number(totals.kilocalories)),
      protein: Math.round(number(totals.protein)),
      carbs: Math.round(number(totals.carbs)),
      fat: Math.round(number(totals.fat)),
    };
  }), [menu.currentWeekStart, menu.weeklyData]);

  const date = useMemo(() => new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date()), []);

  const openNewMeal = () => {
    setEditingMeal(null);
    setAnalyzerOpen(true);
  };

  const openAddPopup = (meal) => setAddPopup({ open: true, meal });
  const closeAddPopup = () => setAddPopup({ open: false, meal: null });

  if (menu.loading) {
    return (
      <div className="dash2-loading">
        <CircularProgress size={30} thickness={4} />
        <span>Preparando tu día…</span>
      </div>
    );
  }

  const goals = userInfo?.macros || {};
  const hasMeals = mealEntries.some(([, meal]) => meal?.items?.length);

  return (
    <ExperimentalPopupProvider>
      <Box className="dash2-shell">
      <div className="dash2-page">
        <PageHeader name={userInfo?.name?.split(' ')[0] || 'Usuario'} date={date} />

        <MobileQuickActions onAddMeal={openNewMeal} onMeasurement={() => setMeasurementOpen(true)} />

        {!hasCompletedOnboarding && (
          <Link className="dash2-onboarding" to="/onboarding">
            <span><AutoAwesomeRounded /></span>
            <div><strong>Completa tu perfil</strong><small>Activa objetivos y recomendaciones personalizadas.</small></div>
            <ArrowForwardRounded />
          </Link>
        )}

        <TodayOverview macros={todayMacros} goals={goals} onAddMeal={openNewMeal} />

        <div className="dash2-main-grid">
          <TodaySchedule
            meals={mealEntries}
            error={menu.error}
            onAddMeal={openAddPopup}
            onEditMeal={(item, mealName) => {
              setEditingMeal({ ...item, meal: item.meal || mealName });
              setAnalyzerOpen(true);
            }}
            onRemoveMeal={(itemId, mealName) => menu.actions.removeFoodFromMeal(todayKey, mealName, itemId)}
          />
          <aside className="dash2-rail">
            <NextStep hasMeals={hasMeals} onAddMeal={openNewMeal} onMeasurement={() => setMeasurementOpen(true)} />
            <QuickLinks />
          </aside>
        </div>

        <div className="dash2-insights-heading">
          <div>
            <span className="dash2-kicker">Tendencias</span>
            <h2>Una mirada más amplia</h2>
          </div>
          <span><InsightsRounded /> Tus datos se actualizan automáticamente</span>
        </div>

        <div className="dash2-insights-grid">
          <WeeklyChart data={weeklyStats} goals={goals} />
          <div className="dash2-body-history">
            <BodyHistoryChart userMetrics={userMetrics} refreshKey={metricsRefreshKey} />
          </div>
        </div>

        <footer className="dash2-footer-note">
          <LocalFireDepartmentRounded />
          <span>La constancia gana a la perfección. Cada registro cuenta.</span>
        </footer>
      </div>

      <AddMealPopup
        open={addPopup.open}
        onClose={closeAddPopup}
        onSelect={async (selection) => {
          if (!addPopup.meal) return;
          await menu.actions.addFoodToMeal(todayKey, addPopup.meal, selection);
        }}
      />

      <AIFoodAnalyzer
        open={analyzerOpen}
        onClose={() => {
          setAnalyzerOpen(false);
          setEditingMeal(null);
        }}
        selectedDate={new Date()}
        selectedMeal={editingMeal?.meal}
        existingMeal={editingMeal}
        onSuccess={async () => {
          await menu.actions.refresh();
          setAnalyzerOpen(false);
          setEditingMeal(null);
        }}
      />

      <QuickMeasurementDialog
        open={measurementOpen}
        onClose={() => setMeasurementOpen(false)}
        userMetrics={userMetrics}
        onSaved={() => setMetricsRefreshKey((key) => key + 1)}
      />

        <OnboardingPromptModal open={showOnboardingModal} onClose={dismissOnboardingModal} />
      </Box>
    </ExperimentalPopupProvider>
  );
};

export default DashboardV2;
